import React from 'react'
import styled from 'styled-components'
import { darken } from 'polished'

// Configs
import { borrowingBaseCertificateConfig } from '../_configs'
import colors from '../_constants/colors'

// Utils
import { calculateTotalEligible, calculateLastTotalEligible, calculateTotalMargined, parseNumber } from './helper'
import { formatCurrency, formatIntPercentage } from '../_helpers'

// Components
import { BBCAssetFormRow, BBCIneligibleFormRow, BBCTotalsRow } from './BBCFormRow'
import { Button } from '../_components/Button'
import { BBCSectionHeader } from './BBCSectionHeader'
import { StyledBBCFormControls } from './BBCFormControls'
import { InvoiceManager, S_InvoiceManager } from '../_components/InvoiceManager'
import { BBCDocumentsViewer } from './BBCDocumentsViewer'

const StyledBBCFormSection = styled.div`
  outline: none;

  ${S_InvoiceManager} {
    margin-bottom: 30px;
  }

  .section-fields {
    margin-bottom: 50px;
  }

  .last-total-value,
  .total-value {
    text-align: right;
  }

  .total-value {
    padding-left: 1em;
  }

  .section-totals-divider {
    border-top: 1px solid #ddd;
    margin: 20px 0;
  }

  .section-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    span,
    button {
      margin: 0 20px;
    }
    .back-btn {
      opacity: 0.6;
      font-size: 14px;
      text-decoration: underline;
      cursor: pointer;
      transition: all 0.2s ease-in-out;
      &:hover {
        opacity: 0.75;
      }
    }
    .review-btn {
      background-color: ${colors.MATERIAL_GREEN};
      border-color: ${darken(0.1, colors.MATERIAL_GREEN)};
      &:hover {
        background-color: ${darken(0.1, colors.MATERIAL_GREEN)};
      }
    }
  }
`

export const BBCFormSection = ({
  formSection = {},
  sectionAsset = {},
  ineligibles = {},
  handleUpdateAssetValue = () => {},
  handleUpdateAssetDate = () => {},
  handleUpdateIneligibleValue = () => {},
  activeSection = 0,
  currentSection = 0,
  onSectionChange = undefined,
  numSections = 1,
  handleSectionChange = () => {},
  handleGoToReview = () => {},
  handleSaveItemData = () => {},
  showLast = true,
  invoices = [],
  documents = [],
  handleAddDocument = () => {},
  bbcId = null,
}) => {
  // Get ineligables for section asset
  const sectionIneligibles = sectionAsset.ineligibles
    ? sectionAsset.ineligibles.map((ineligiblesId) => {
        return ineligibles[ineligiblesId]
      })
    : []

  // Calculate section totals
  const lastTotalEligible = calculateLastTotalEligible(sectionAsset, ineligibles)
  const totalEligible = calculateTotalEligible(sectionAsset, ineligibles)
  const advancedRate = sectionAsset.advance_rate
  const lastAdvancedRate = sectionAsset.last_advance_rate
  const totalMarginedAmount = calculateTotalMargined(totalEligible, advancedRate)
  const lastTotalMarginedAmount = calculateTotalMargined(lastTotalEligible, lastAdvancedRate)

  return (
    <StyledBBCFormSection active={false /*activeSection === i + 1*/} className={`section`}>
      {formSection.section_type === 'Accounts Receivable' && <InvoiceManager invoices={invoices} />}

      <BBCSectionHeader
        colHeaders={[borrowingBaseCertificateConfig.lastSubTitle, borrowingBaseCertificateConfig.newSubTitle]}
        showLast={showLast}
        title={formSection.title}
      />

      <div className={`section-fields`}>
        {/* Section Asset */}
        <BBCAssetFormRow
          asset={sectionAsset}
          handleBlur={handleSaveItemData}
          inputTestID={'bbc-asset-input'}
          label={formSection.fields[1].label}
          onDateChange={(e) => {
            handleUpdateAssetDate(sectionAsset.id, e.target.value)
          }}
          onValueChange={(e) => {
            const numericValue = parseNumber(e.target.value)
            handleUpdateAssetValue(sectionAsset.id, numericValue)
          }}
          showLast={showLast}
        />

        {/* Section Ineligibles */}
        {sectionIneligibles.map((ineligible, i) => {
          return (
            <BBCIneligibleFormRow
              handleBlur={handleSaveItemData}
              ineligible={ineligible}
              key={ineligible.id}
              onValueChange={(e) => {
                const numericValue = parseNumber(e.target.value)
                handleUpdateIneligibleValue(ineligible.id, numericValue)
              }}
              showLast={showLast}
            />
          )
        })}

        <div className="section-totals-divider" />

        {/* Section Totals */}
        <BBCTotalsRow
          label={formSection.totalCaption}
          lastValue={formatCurrency(lastTotalEligible)}
          showLast={showLast}
          value={formatCurrency(totalEligible)}
        />
        <BBCTotalsRow
          label={formSection.rateTitle}
          lastValue={formatIntPercentage(lastAdvancedRate)}
          showLast={showLast}
          value={formatIntPercentage(advancedRate)}
        />
        <BBCTotalsRow
          label={formSection.marginedAmount}
          lastValue={formatCurrency(lastTotalMarginedAmount)}
          showLast={showLast}
          value={formatCurrency(totalMarginedAmount)}
        />
      </div>

      {/* Documents */}
      <BBCDocumentsViewer
        addDocument={handleAddDocument}
        bbcId={bbcId}
        documents={documents}
        fileType={formSection.section_type}
      />

      {/* Controls to Navigate between sections */}
      <StyledBBCFormControls>
        {activeSection > 1 && (
          <span
            className={`back-btn`}
            onClick={() => {
              handleSectionChange(activeSection - 1)
              currentSection = currentSection - 1
              onSectionChange(currentSection)
            }}
          >
            Back
          </span>
        )}
        {activeSection < numSections && (
          <Button
            buttonType={`button`}
            onClick={() => {
              handleSectionChange(activeSection + 1)
              currentSection = currentSection + 1
              onSectionChange(currentSection)
            }}
            text={`Next`}
            type={`button`}
          />
        )}
        {activeSection === numSections && (
          <Button buttonType={`button`} onClick={() => handleGoToReview()} text={`Review`} type={`button`} />
        )}
      </StyledBBCFormControls>
    </StyledBBCFormSection>
  )
}
